import { useFlag } from '../../hooks'
import { creditCardsService } from '@afterpaytouch/portal-api/consumer/account/creditcards'
import { createModalHook } from '../../hooks/useModal'
import { useCallback, useEffect } from 'react'
import { ReportCardCancellationSteps, useReportCardCancellationModalContext } from './ReportCardCancellationModalContext'
import { useToast } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { transformDate } from './utils'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'

export interface UseReportCardCancellationModal {
  reportCancellationCtaVisible: boolean
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
}

const I18N_NAMESPACE = ['account']

const useModal = createModalHook()

export const useReportCardCancellationModal = (): UseReportCardCancellationModal => {
  const reportCancellationCtaVisible: boolean = useFlag('ir-report-lost-and-stolen-card-new-consumer-button-enabled')
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const [isModalOpen, { openModal: open, closeModal }] = useModal()

  const openModal = (): void => {
    logEvent(TrackingEvent.PRESSED_REPORT_CARD_CANCELLATION)
    open()
  }

  return {
    reportCancellationCtaVisible,
    isModalOpen,
    openModal,
    closeModal,
  }
}

const { useInitialReportCardCancellationMutation, useConfirmReportCardCancellationMutation } = creditCardsService
interface UseReportCardCancellation {
  init: () => any
  confirm: () => any
}

export const useReportCardCancellation = (): UseReportCardCancellation => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const [initReportCardCancellation, { data: initResult, isLoading: isInitLoading, isError: isInitError, isSuccess: isInitSuccess }] =
    useInitialReportCardCancellationMutation()
  const [confirmReportCardCancellation, { data: confirmResult, isLoading: isConfirmLoading, isError: isConfirmError, isSuccess: isConfirmSuccess }] =
    useConfirmReportCardCancellationMutation()
  const { step, setStep, setIsLoading, closeModal, selectedCardIds, creditCards, setCreditCards, refetchCard } = useReportCardCancellationModalContext()
  const setToast = useToast()

  const confirm = useCallback(async () => {
    const allCardsFlag = selectedCardIds.length === creditCards.length
    logEvent(allCardsFlag ? TrackingEvent.PRESSED_REPORT_CARD_CANCELLATION_ALL : TrackingEvent.PRESSED_REPORT_CARD_CANCELLATION_PARTIAL)
    return await confirmReportCardCancellation({
      allCardsFlag,
      cardIdList: selectedCardIds,
    })
  }, [confirmReportCardCancellation, creditCards.length, logEvent, selectedCardIds])

  useEffect(() => {
    setIsLoading(isInitLoading || isConfirmLoading)
  }, [isConfirmLoading, isInitLoading, setIsLoading])

  useEffect(() => {
    if (step === ReportCardCancellationSteps.Initial && isInitSuccess) {
      // @ts-ignore: OPERATION BLEED STOPPER
      if (initResult.canCancel) {
        setStep(ReportCardCancellationSteps.SelectCard)
        // @ts-ignore: OPERATION BLEED STOPPER
        setCreditCards(initResult.creditCardList)
      } else {
        logEvent(TrackingEvent.REPORT_CARD_CANCELLATION_DECLINE)
        setStep(ReportCardCancellationSteps.Decline)
      }
    }
  }, [step, isInitSuccess, initResult, setStep, setCreditCards, logEvent])

  useEffect(() => {
    if (step === ReportCardCancellationSteps.Confirm && isConfirmSuccess) {
      // @ts-ignore: OPERATION BLEED STOPPER
      const message = confirmResult.allCardsFlag
        ? t('account:tab:payment-methods:reportCardCancellationModal:result:success:allCard', {
            // @ts-ignore: OPERATION BLEED STOPPER
            dueDateOfAddCard: transformDate(confirmResult.dueDateOfAddCard),
          })
        : t('account:tab:payment-methods:reportCardCancellationModal:result:success:partialCard')
      setToast({
        message,
        kind: 'Success',
        manualDismiss: true,
      })
      // @ts-ignore: OPERATION BLEED STOPPER
      logEvent(confirmResult.allCardsFlag ? TrackingEvent.REPORT_CARD_CANCELLATION_SUCCESS_ALL : TrackingEvent.REPORT_CARD_CANCELLATION_SUCCESS_PARTIAL)
      refetchCard()
      closeModal()
    }
  }, [step, isConfirmSuccess, confirmResult, setToast, t, closeModal, refetchCard, logEvent])

  useEffect(() => {
    if (isConfirmError || isInitError) {
      setToast({
        message: t('account:tab:payment-methods:reportCardCancellationModal:result:error'),
        kind: 'Failure',
        manualDismiss: true,
      })
      closeModal()
    }
  }, [closeModal, isConfirmError, isInitError, setToast, t])

  return {
    init: initReportCardCancellation,
    confirm,
  }
}
